/**
 * Headline KPI tiles for the FP Monthly COGS Tracker.
 *
 * Four tiles, in order: Done this month / Needs review this month /
 * Months outstanding / Active FPs without tool. The first two are
 * derived per-render from the rows (current calendar month against
 * row.months[key]); the last two come from summary._kpi which the
 * backend pre-aggregates over the requested window.
 */
import React from 'react'
import { Box, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import type { CogsTrackerKpis, CogsTrackerRow } from './types'

interface CogsTrackerKpiRowProps {
  kpis: CogsTrackerKpis | null
  rows: CogsTrackerRow[]
  loading?: boolean
}

type Tone = 'success' | 'warning' | 'error' | 'neutral'

interface Tile {
  label: string
  value: string
  tooltip?: string
  tone: Tone
}

const TONE_BORDER: Record<Tone, string> = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  neutral: 'divider',
}

const TONE_VALUE_COLOUR: Record<Tone, string> = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  neutral: 'text.primary',
}

const currentMonthKey = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const countCurrentMonth = (
  rows: CogsTrackerRow[],
  monthKey: string,
  state: 'posted' | 'needs_review',
): number =>
  rows.reduce((acc, row) => {
    const cell = row.months?.[monthKey]
    return cell?.state === state ? acc + 1 : acc
  }, 0)

const CogsTrackerKpiRow: React.FC<CogsTrackerKpiRowProps> = ({
  kpis,
  rows,
  loading,
}) => {
  if (loading && !kpis) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Loading KPIs...
          </Typography>
        </CardContent>
      </Card>
    )
  }
  if (!kpis) return null

  const monthKey = currentMonthKey()
  const inWindow = (kpis.window_months || []).includes(monthKey)
  const doneThisMonth = inWindow ? countCurrentMonth(rows, monthKey, 'posted') : null
  const reviewThisMonth = inWindow
    ? countCurrentMonth(rows, monthKey, 'needs_review')
    : null

  const formatCount = (n: number | null): string =>
    n === null ? '-' : n.toLocaleString()

  const tiles: Tile[] = [
    {
      label: 'Done this month',
      value: formatCount(doneThisMonth),
      tooltip: inWindow
        ? 'FPs whose current-month COGS reconciliation is posted.'
        : 'Current month is outside the selected window.',
      tone: 'success',
    },
    {
      label: 'Needs review this month',
      value: formatCount(reviewThisMonth),
      tooltip: inWindow
        ? 'FPs whose current-month sheet is flagged Needs review.'
        : 'Current month is outside the selected window.',
      tone: 'error',
    },
    {
      label: 'Months outstanding',
      value: kpis.months_outstanding_total.toLocaleString(),
      tooltip:
        'Months in window where status is Ready to post or Needs review.',
      tone: 'neutral',
    },
    {
      label: 'Active FPs without tool',
      value: kpis.fps_active_without_tool.toLocaleString(),
      tooltip:
        'Active FPs with no Monthly COGS Tool yet - investigate before more months go unreconciled.',
      tone: 'error',
    },
  ]

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
      {tiles.map((tile) => {
        const showWarn =
          (tile.tone === 'error' || tile.tone === 'warning') &&
          tile.value !== '0' &&
          tile.value !== '-'
        return (
          <Tooltip key={tile.label} title={tile.tooltip ?? ''} placement="top">
            <Card
              sx={{
                flex: 1,
                borderLeft: 4,
                borderLeftColor: TONE_BORDER[tile.tone],
                borderColor: 'divider',
              }}
            >
              <CardContent sx={{ py: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {tile.label}
                  </Typography>
                  {showWarn ? (
                    <WarningAmberIcon
                      fontSize="inherit"
                      color={tile.tone === 'warning' ? 'warning' : 'error'}
                      aria-label="warning"
                    />
                  ) : null}
                </Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: TONE_VALUE_COLOUR[tile.tone] }}
                >
                  {tile.value}
                </Typography>
              </CardContent>
            </Card>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

export default CogsTrackerKpiRow
